import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StaffKpiProgressCard from "../components/staff/StaffKpiProgressCard";
import ConfirmModal from "../components/modals/ConfirmModal";
import ImageUploadField from "../components/ImageUploadField";
import { useAuth } from "../context/AuthContext";
import {
  changeMyPassword,
  getMyKpiProgress,
  getMyProfile,
  removeMyProfilePhoto,
  updateMyProfile,
  uploadMyProfilePhoto,
} from "../api/profile";
import { formatNANP, isValidNANP, toNANPDigits10 } from "../utils/phone";

const DEFAULT_PROFILE = {
  user_id: null,
  role: "",
  full_name: "",
  email: "",
  username: "",
  current_salon_id: null,
  current_salon_name: "",
  current_business_id: null,
  current_business_name: "",
  staff_id: null,
  salon_display_name: "",
  phone: "",
  photo_url: "",
  has_current_salon_profile: false,
  linked_salon_ids: [],
  linked_business_ids: [],
};

const normalizeIdList = (value) => {
  const entries = Array.isArray(value) ? value : [];
  return [
    ...new Set(entries.map((entry) => Number(entry)).filter(Number.isFinite)),
  ].sort((a, b) => a - b);
};

const splitFullName = (fullName) => {
  const value = String(fullName || "").trim();
  if (!value) {
    return { first_name: "", last_name: "" };
  }
  const parts = value.split(/\s+/);
  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" "),
  };
};

const joinFullName = (firstName, lastName) =>
  [String(firstName || "").trim(), String(lastName || "").trim()]
    .filter(Boolean)
    .join(" ");

const normalizeProfile = (payload = {}) => {
  const phoneDigits = toNANPDigits10(payload?.phone || "");
  const nameParts = splitFullName(payload?.full_name || "");
  return {
    user_id: Number(payload?.user_id || 0) || null,
    role: String(payload?.role || "").toLowerCase(),
    full_name: payload?.full_name || "",
    first_name: nameParts.first_name,
    last_name: nameParts.last_name,
    email: payload?.email || "",
    username: payload?.username || "",
    current_salon_id: Number(payload?.current_salon_id || 0) || null,
    current_salon_name: payload?.current_salon_name || "",
    current_business_id: Number(payload?.current_business_id || 0) || null,
    current_business_name: payload?.current_business_name || "",
    staff_id: Number(payload?.staff_id || 0) || null,
    salon_display_name: payload?.salon_display_name || "",
    phone: phoneDigits || "",
    photo_url: payload?.photo_url || "",
    has_current_salon_profile: Boolean(payload?.has_current_salon_profile),
    linked_salon_ids: normalizeIdList(payload?.linked_salon_ids),
    linked_business_ids: normalizeIdList(payload?.linked_business_ids),
  };
};

const roleLabel = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "User";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const messageForError = (message) => {
  switch (message) {
    case "CURRENT_SALON_PROFILE_REQUIRED":
      return "No current salon profile is linked to this login yet.";
    case "FULL_NAME_REQUIRED":
      return "Full name is required.";
    case "USERNAME_INVALID":
      return "Username can only use letters, numbers, underscores, or hyphens.";
    case "USERNAME_TAKEN":
      return "That username is already in use.";
    case "SALON_DISPLAY_NAME_REQUIRED":
      return "Display name is required for the current salon profile.";
    case "SALON_CONTEXT_REQUIRED":
      return "Select a salon before editing salon-specific profile details.";
    case "INVALID_CURRENT_PASSWORD":
      return "Current password is incorrect.";
    default:
      return message;
  }
};

const badgeClass =
  "inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700";

export default function Profile() {
  const navigate = useNavigate();
  const { token, user, logout, updateCurrentUser } = useAuth();
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [kpiProgress, setKpiProgress] = useState(null);
  const [loadingKpiProgress, setLoadingKpiProgress] = useState(false);
  const [kpiProgressError, setKpiProgressError] = useState("");

  const loadProfile = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setLoadingKpiProgress(true);
    setSaveError("");
    try {
      const [profileResult, kpiResult] = await Promise.allSettled([
        getMyProfile(token),
        getMyKpiProgress(token),
      ]);
      if (profileResult.status === "fulfilled") {
        setProfile(normalizeProfile(profileResult.value));
      } else {
        setSaveError(
          messageForError(
            profileResult.reason?.message || "Unable to load your profile.",
          ),
        );
      }
      if (kpiResult.status === "fulfilled") {
        setKpiProgress(kpiResult.value);
        setKpiProgressError("");
      } else {
        setKpiProgress(null);
        setKpiProgressError(
          messageForError(
            kpiResult.reason?.message || "Unable to load KPI progress.",
          ),
        );
      }
    } finally {
      setLoading(false);
      setLoadingKpiProgress(false);
    }
  }, [token]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleFieldChange = (field, value) => {
    if (field === "phone" && phoneError) {
      setPhoneError("");
    }
    setSaveError("");
    setSaveSuccess("");
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const applyProfileResponse = useCallback(
    (data) => {
      const normalized = normalizeProfile(data);
      setProfile(normalized);
      updateCurrentUser({
        full_name: normalized.full_name,
        email: normalized.email,
        photo_url: normalized.photo_url,
      });
    },
    [updateCurrentUser],
  );

  const handleUploadPhoto = async (file) => {
    if (!token) return "";
    const data = await uploadMyProfilePhoto(file, token);
    applyProfileResponse(data);
    return data?.photo_url || "";
  };

  const handleRemovePhoto = async () => {
    if (!token) return "";
    const data = await removeMyProfilePhoto(token);
    applyProfileResponse(data);
    return data?.photo_url || "";
  };

  const handleSaveProfile = async (event) => {
    event?.preventDefault?.();
    if (!token) return;

    const phoneDigits = profile.phone ? toNANPDigits10(profile.phone) : "";
    if (phoneDigits && !isValidNANP(phoneDigits)) {
      setPhoneError("Phone must be a 10-digit North American number.");
      return;
    }

    setSaving(true);
    setSaveError("");
    setSaveSuccess("");
    try {
      const fullName = joinFullName(profile.first_name, profile.last_name);
      const payload = {
        full_name: fullName,
        email: profile.email.trim(),
        username: profile.username.trim() || null,
      };
      if (profile.has_current_salon_profile) {
        payload.salon_display_name = profile.salon_display_name.trim();
        payload.phone = phoneDigits || null;
      }
      const data = await updateMyProfile(payload, token);
      applyProfileResponse(data);
      setSaveSuccess("Profile saved.");
    } catch (err) {
      setSaveError(
        messageForError(err?.message || "Unable to save your profile."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!newPassword || newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }
    if (!token) {
      setPasswordError("You are no longer signed in.");
      return;
    }

    setChangingPassword(true);
    try {
      await changeMyPassword(
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        token,
      );
      setPasswordSuccess("Password updated. You will need to sign in again.");
      setShowPasswordModal(true);
    } catch (err) {
      setPasswordError(
        messageForError(err?.message || "Could not change password."),
      );
    } finally {
      setChangingPassword(false);
    }
  };

  const linkedSalonIds = useMemo(
    () => normalizeIdList(profile.linked_salon_ids),
    [profile.linked_salon_ids],
  );
  const linkedBusinessIds = useMemo(
    () => normalizeIdList(profile.linked_business_ids),
    [profile.linked_business_ids],
  );

  const handlePasswordAcknowledge = async () => {
    setShowPasswordModal(false);
    await logout();
    navigate("/login");
  };

  return (
    <div className="manageSectionShell min-h-screen bg-slate-100">
      <div className="manageSectionWide manage-section-wide manage-section-compact">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              My Profile
            </h1>
            <p className="text-sm md:text-base text-white/80">
              Update your account details, current salon profile, password, and
              linked salon records.
            </p>
          </div>

          <div className="px-6 py-6 space-y-6 text-sm md:text-base">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Profile overview
                  </div>
                  <p className="mt-1 max-w-4xl text-xs leading-5 text-slate-600">
                    Update your account details, manage the current salon
                    profile tied to this login, and review the salon and
                    business IDs linked to you.
                  </p>
                </div>
                <div className="w-fit rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                  <div>
                    <span className="font-semibold text-slate-900">Role:</span>{" "}
                    {roleLabel(profile.role || user?.role)}
                  </div>
                  <div className="mt-1">
                    <span className="font-semibold text-slate-900">
                      Current Salon:
                    </span>{" "}
                    {profile.current_salon_name || "None selected"}
                    {profile.current_salon_id
                      ? ` (ID ${profile.current_salon_id})`
                      : ""}
                  </div>
                </div>
              </div>
            </div>

            {(linkedSalonIds.length > 0 || linkedBusinessIds.length > 0) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-lg font-semibold text-slate-900">
                  Linked IDs
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  These are the salon and business records currently linked to
                  this login.
                </p>
                <div className="mt-4 grid gap-5 md:grid-cols-2">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Salon IDs
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {linkedSalonIds.length > 0 ? (
                        linkedSalonIds.map((id) => (
                          <span key={`salon-${id}`} className={badgeClass}>
                            {id}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-400">None</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Business IDs
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {linkedBusinessIds.length > 0 ? (
                        linkedBusinessIds.map((id) => (
                          <span key={`business-${id}`} className={badgeClass}>
                            {id}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-400">None</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <StaffKpiProgressCard
              title="Targeted KPI"
              subtitle="Review your current month progress against the KPI targets assigned to your current salon profile."
              cardClass="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              progress={kpiProgress}
              loading={loadingKpiProgress}
              error={kpiProgressError}
            />

            {(saveError || saveSuccess || passwordError || passwordSuccess) && (
              <div className="space-y-3">
                {saveError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {saveError}
                  </div>
                )}
                {saveSuccess && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    {saveSuccess}
                  </div>
                )}
                {passwordError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {passwordError}
                  </div>
                )}
                {passwordSuccess && !showPasswordModal && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    {passwordSuccess}
                  </div>
                )}
              </div>
            )}

            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              <form
                onSubmit={handleSaveProfile}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-6"
              >
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">
                    Account Info
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    These details belong to your login and follow you across
                    linked salons.
                  </p>
                </div>

                {loading ? (
                  <div className="text-sm text-slate-500">
                    Loading your profile...
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-sm font-semibold text-slate-700">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={profile.first_name}
                          onChange={(event) =>
                            handleFieldChange("first_name", event.target.value)
                          }
                          className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-700">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={profile.last_name}
                          onChange={(event) =>
                            handleFieldChange("last_name", event.target.value)
                          }
                          className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                          placeholder="Last name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-700">
                          Email
                        </label>
                        <input
                          type="email"
                          value={profile.email}
                          onChange={(event) =>
                            handleFieldChange("email", event.target.value)
                          }
                          className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                          placeholder="you@example.com"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-700">
                          Username
                        </label>
                        <input
                          type="text"
                          value={profile.username}
                          onChange={(event) =>
                            handleFieldChange("username", event.target.value)
                          }
                          className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                          placeholder="username"
                        />
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className="text-base font-semibold text-slate-900">
                            Current Salon Profile
                          </h3>
                          <p className="mt-1 text-sm text-slate-500">
                            These fields only apply to your selected salon
                            context.
                          </p>
                        </div>
                        <div className="text-xs uppercase tracking-wide text-slate-400">
                          {profile.current_salon_name || "No salon selected"}
                        </div>
                      </div>

                      {profile.has_current_salon_profile ? (
                        <div className="mt-5 space-y-4">
                          <ImageUploadField
                            label="Profile Picture"
                            helper="Shown in the navbar and other current-salon staff views."
                            recommended="Square image recommended"
                            value={profile.photo_url}
                            onChange={(next) =>
                              handleFieldChange("photo_url", next || "")
                            }
                            onUpload={handleUploadPhoto}
                            onRemove={handleRemovePhoto}
                            allowUrlPaste={false}
                            maxSizeMb={2}
                          />

                          <div className="grid gap-4 md:grid-cols-2">
                            <div>
                              <label className="text-sm font-semibold text-slate-700">
                                Current Salon Display Name
                              </label>
                              <input
                                type="text"
                                value={profile.salon_display_name}
                                onChange={(event) =>
                                  handleFieldChange(
                                    "salon_display_name",
                                    event.target.value,
                                  )
                                }
                                className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                placeholder="Name shown in this salon"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-semibold text-slate-700">
                                Current Salon Phone
                              </label>
                              <input
                                type="text"
                                value={
                                  isValidNANP(profile.phone)
                                    ? formatNANP(profile.phone)
                                    : profile.phone
                                }
                                onChange={(event) =>
                                  handleFieldChange(
                                    "phone",
                                    toNANPDigits10(event.target.value),
                                  )
                                }
                                className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                placeholder="(555) 123-4567"
                              />
                              {phoneError && (
                                <p className="mt-1 text-xs text-rose-600">
                                  {phoneError}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                          {profile.current_salon_id
                            ? "This login does not currently have a staff profile in the selected salon, so salon-specific display name, phone, and photo fields are unavailable."
                            : "Select a salon first if you want to manage salon-specific profile details."}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={saving}
                        className="rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-70"
                      >
                        {saving ? "Saving..." : "Save Profile"}
                      </button>
                    </div>
                  </>
                )}
              </form>

              <form
                onSubmit={handlePasswordSubmit}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-5"
              >
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">
                    Change Password
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Update your login password here. You&apos;ll sign in again
                    after the change succeeds.
                  </p>
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-700">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    placeholder="At least 8 characters"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    required
                  />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Password changes are account-wide and apply to every salon
                  linked to this login.
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="rounded-full bg-rose-500 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-600 disabled:opacity-70"
                  >
                    {changingPassword ? "Updating..." : "Update Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showPasswordModal}
        title="Password Updated"
        message="Your password has been updated successfully. Please log in again."
        confirmText="OK"
        hideCancel
        showClose={false}
        onConfirm={handlePasswordAcknowledge}
        onClose={handlePasswordAcknowledge}
      />
    </div>
  );
}
